;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();import type {
  LandingFormatFilter,
  LandingGroupQueryInput,
  LandingGroupSort,
  LandingPageDocument,
  LandingPageType,
  PublicLandingGroup,
  SearchGroupsPublicVariables,
} from "./types"

// Matches the radius the homepage search pins (searchGroupsPublic.ts).
export const DEFAULT_LANDING_RADIUS_MI = 100

const SUPPORTED_GROUP_TYPES = new Set([
  "THERAPY_GROUP",
  "SUPPORT_GROUP",
  "WORKSHOP",
  "RETREAT",
  "PROVIDER_GROUP",
])

export const LANDING_PAGE_TYPES: LandingPageType[] = [
  "group_type_landing_page",
  "topic_population_landing_page",
  "location_landing_page",
]

export function isLandingPageType(type: string): type is LandingPageType {
  return LANDING_PAGE_TYPES.includes(type as LandingPageType)
}

function compactStrings(values: Array<string | null | undefined>) {
  return values
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean)
}

function normalizeGroupType(value?: string | null) {
  const normalized = value?.trim()
  if (!normalized || !SUPPORTED_GROUP_TYPES.has(normalized)) return null
  return normalized
}

function normalizeFormatFilter(value?: LandingFormatFilter | null) {
  return value || "all"
}

export function getLandingPageGroupTypes(page: LandingPageDocument) {
  const groupTypes = new Set<string>()
  const data = page.data

  const directGroupType = normalizeGroupType(data.group_type)
  if (directGroupType) groupTypes.add(directGroupType)
  ;(data.group_types || []).forEach((entry) => {
    const groupType = normalizeGroupType(entry?.group_type)
    if (groupType) groupTypes.add(groupType)
  })

  return Array.from(groupTypes)
}

export function getLandingPageFeaturedGroupIds(page: LandingPageDocument) {
  return (page.data.featured_group_ids || [])
    .map((entry) => entry?.group_id)
    .filter((groupId): groupId is number => typeof groupId === "number")
}

export function buildLandingGroupQueryInput(
  page: LandingPageDocument,
  overrides: Partial<LandingGroupQueryInput> = {},
): LandingGroupQueryInput {
  const data = page.data
  const formatFilter = normalizeFormatFilter(data.format_filter)
  const groupTypes = getLandingPageGroupTypes(page)

  // Both flags are always explicit: searchGroupsPublicV2 treats "both true"
  // as no format filter, and omitting them is not the same thing.
  const format =
    formatFilter === "virtual"
      ? { virtual: true, inPerson: false }
      : formatFilter === "in_person"
        ? { virtual: false, inPerson: true }
        : { virtual: true, inPerson: true }

  return {
    first: 12,
    ...(groupTypes.length ? { groupTypes } : {}),
    ...(data.search_query ? { search: data.search_query } : {}),
    ...format,
    ...(data.state ? { state: data.state } : {}),
    ...(typeof data.latitude === "number" ? { latitude: data.latitude } : {}),
    ...(typeof data.longitude === "number" ? { longitude: data.longitude } : {}),
    ...(typeof data.radius_mi === "number" ? { radiusMi: data.radius_mi } : {}),
    ...overrides,
  }
}

function sortToApi(sort?: LandingGroupSort | null) {
  switch (sort) {
    case "newest":
      return "newest" as const
    case "relevance":
    case "next_start_date":
      return "upcoming" as const
    default:
      return undefined
  }
}

// Translates page intent into the exact variables of searchGroupsPublicLanding.
// groupIds is deliberately absent: manual picks are filtered client-side.
export function toSearchGroupsVariables(
  input: LandingGroupQueryInput,
): SearchGroupsPublicVariables {
  const iSort = sortToApi(input.sort)
  const state = input.state?.trim().toUpperCase()

  return {
    ...(input.first ? { first: input.first } : {}),
    ...(input.groupTypes?.length ? { groupTypes: input.groupTypes } : {}),
    inPerson: input.inPerson ?? true,
    virtual: input.virtual ?? true,
    ...(input.search ? { search: input.search } : {}),
    ...(typeof input.latitude === "number" ? { latitude: input.latitude } : {}),
    ...(typeof input.longitude === "number" ? { longitude: input.longitude } : {}),
    radiusMi:
      typeof input.radiusMi === "number" && input.radiusMi > 0
        ? Math.round(input.radiusMi)
        : DEFAULT_LANDING_RADIUS_MI,
    ...(state ? { iState: state } : {}),
    ...(input.publishedAfter ? { iPublishedAfter: input.publishedAfter } : {}),
    ...(iSort ? { iSort } : {}),
  }
}

export function groupIdentity(group: PublicLandingGroup) {
  return group.groupId == null ? "" : String(group.groupId)
}

export function filterConsumedGroups(
  groups: PublicLandingGroup[],
  consumedGroupIds?: Set<string>,
) {
  if (!consumedGroupIds) return groups
  return groups.filter((group) => !consumedGroupIds.has(groupIdentity(group)))
}

export function rememberRenderedGroups(
  groups: PublicLandingGroup[],
  consumedGroupIds?: Set<string>,
) {
  if (!consumedGroupIds) return

  groups.forEach((group) => {
    const identity = groupIdentity(group)
    if (identity) consumedGroupIds.add(identity)
  })
}

export function uniqueGroups(groups: PublicLandingGroup[]) {
  const seen = new Set<string>()
  return groups.filter((group) => {
    const identity = groupIdentity(group)
    if (!identity) return false
    if (seen.has(identity)) return false
    seen.add(identity)
    return true
  })
}

export function limitGroups(
  groups: PublicLandingGroup[],
  maxGroups?: number | null,
) {
  const limit =
    typeof maxGroups === "number" && Number.isFinite(maxGroups) && maxGroups > 0
      ? maxGroups
      : groups.length

  return groups.slice(0, limit)
}

export function publicGroupMatchesManualIds(
  group: PublicLandingGroup,
  groupIds: number[],
) {
  if (!groupIds.length) return true
  const groupId = group.groupId == null ? null : Number(group.groupId)
  return groupId != null && groupIds.includes(groupId)
}

export function buildLandingCanonicalUrl(page: LandingPageDocument) {
  const override = page.data.canonical_override
  if (override && "url" in override && override.url) {
    return override.url
  }

  return `https://getzestlife.com${page.url || landingPathForPage(page)}`
}

export function landingPathForPage(page: Pick<LandingPageDocument, "type" | "uid">) {
  switch (page.type) {
    case "topic_population_landing_page":
      return `/groups/${page.uid}`
    case "location_landing_page":
      return `/therapy-groups/${page.uid}`
    case "group_type_landing_page":
    default:
      return `/${page.uid}`
  }
}

export function breadcrumbItemsForLandingPage(page: LandingPageDocument) {
  const title = page.data.title || page.uid
  const items = [{ name: "Home", url: "https://getzestlife.com" }]

  if (page.type === "location_landing_page" && page.data.geographic_search) {
    items.push({
      name: "Therapy groups",
      url: "https://getzestlife.com/therapy-groups",
    })
    const parentPath = page.data.parent_state_path
    if (
      page.data.parent_state_label &&
      parentPath &&
      /^\/therapy-groups\/[a-z0-9-]+$/.test(parentPath) &&
      parentPath !== (page.url || landingPathForPage(page))
    ) {
      items.push({
        name: page.data.parent_state_label,
        url: `https://getzestlife.com${parentPath}`,
      })
    }
  }

  items.push({ name: title, url: buildLandingCanonicalUrl(page) })
  return items
}

export function compactLandingKeywords(page: LandingPageDocument) {
  return compactStrings([
    page.data.primary_keyword,
    page.data.search_query,
    page.data.group_type,
    page.data.state,
    page.data.city,
  ])
}
